// src/components/Seller/SellerDataDetail.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom'; 
import config from '../../config';
import useSellerData from './hooks/useSellerdata'; 
import useAdditionalInput from './hooks/useAdditionalInput';
import SellerDetailsCard from './SellerDetailsCard';
import FollowUpAnswers from './FollowUpAnswers';
import ResultsCard from '../Objectinfo/ResultsCard';
import AdditionalInputForm from '../Objectinfo/AdditionalInputForm';
import SellerButtons from './SellerButtons';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import SpinnerOverlay from '../Spinner/spinneroverlay';
import UnifiedFileUpload from './UnifiedFileUpload';
import NewConversation from '../Conversation/NewConversation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faInfoCircle, faComments, faMapMarkerAlt, faPhone, faEnvelope, faHome, faTimes, faArrowLeft, faArrowRight, faUser } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useLanguage } from '../../i18n/LanguageContext';
import { translatePropertyType } from '../../i18n/translations';
import { BodexPlusOffer } from '../BodexPlus/BodexPlus';

function SellerDataDetail({ dataId }) {
  const params = useParams();
  const finalDataId = dataId || params.id;
  const history = useHistory(); // <-- useHistory instead of useNavigate
  const location = useLocation(); // To access URL query parameters
  const { t, language } = useLanguage();

  // Check if we should simulate broker view (for sellers previewing their ad as brokers)
  const searchParams = new URLSearchParams(location.search);
  const simulateBrokerView = searchParams.get('view') === 'broker';

  // Our custom hooks
  const {
    sellerData,
    loadingAd,
    handleSellerDataUpdate,
    handleDeleteSellerData,
    fetchSellerData,
    error,
  } = useSellerData(finalDataId);

  const {
    additionalInput,
    setAdditionalInput,
    showAdditionalInputForm,
    toggleAdditionalInputForm,
    handleAdditionalInputSubmit,
  } = useAdditionalInput(finalDataId);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showConversationForm, setShowConversationForm] = useState(false);
  const [sellerImages, setSellerImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);
  
  // Add refs for the arrow buttons
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);
  const contactInfoCardRef = useRef(null);

  // Get the actual user type from localStorage
  const actualUserType = localStorage.getItem('userType');
  
  // Use the simulated user type if we're in broker view mode, otherwise use the actual user type
  const userType = simulateBrokerView ? 'broker' : actualUserType;

  // 1. Fetch data on mount
  useEffect(() => {
    fetchSellerData();
  }, [fetchSellerData]);

  // 2. Convert existing filenames into absolute URLs
  useEffect(() => {
    if (sellerData && sellerData.multiple_image_filenames) {
      const urls = sellerData.multiple_image_filenames.map((filename) => {
        return `${config.backend_url}/uploads/${filename}`;
      });
      setSellerImages(urls);
    }
  }, [sellerData]);

  // Fetch seller information when sellerData is loaded
  useEffect(() => {
    const fetchSellerInfo = async () => {
      if (sellerData && sellerData.seller_id) {
        try {
          const response = await axios.get(`${config.backend_url}/api/seller/${sellerData.seller_id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });
          setSellerInfo(response.data);
        } catch (error) {
          console.error('Error fetching seller info:', error);
        }
      }
    };
    
    fetchSellerInfo();
  }, [sellerData]);

  // Full-size image modal
  const handleImageClick = (imgUrl) => {
    const index = sellerImages.findIndex(img => img === imgUrl);
    setSelectedImageIndex(index);
    setSelectedImage(imgUrl);
  };

  // Navigate to previous image - implement with useCallback
  const handlePrevImage = useCallback(() => {
    if (selectedImageIndex > 0) {
      const newIndex = selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(sellerImages[newIndex]);
    }
  }, [selectedImageIndex, sellerImages]);

  // Navigate to next image - implement with useCallback
  const handleNextImage = useCallback(() => {
    if (selectedImageIndex < sellerImages.length - 1) {
      const newIndex = selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(sellerImages[newIndex]);
    }
  }, [selectedImageIndex, sellerImages]);

  // Handle keyboard navigation for image modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedImage) return;
      
      if (event.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (event.key === 'ArrowRight') {
        handleNextImage();
      } else if (event.key === 'Escape') {
        setSelectedImage(null);
        setSelectedImageIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, handlePrevImage, handleNextImage]);

  // 3. Handle Delete
  const handleDelete = async () => {
    try {
      // Call our custom hook that does DELETE /api/delete-seller-data/<id>
      await handleDeleteSellerData();
      
      // Navigate to another page after successful delete:
      history.push('/seller-latest'); // or any route you prefer
    } catch (error) {
      console.error('Error deleting seller data:', error);
    }
  };

  // When images are updated by UnifiedFileUpload
  const handleImagesUpdate = (newImages) => {
    setSellerImages(newImages);
    // Extract filenames from URLs to update the backend
    const filenames = newImages.map((url) => {
      const parts = url.split('/');
      return parts[parts.length - 1];
    });
    
    handleSellerDataUpdate({
      multiple_image_filenames: filenames,
    });
  };

  // Top CTA click handler to open conversation form and scroll to contact card
  const handleTopContactClick = () => {
    setShowConversationForm(true);
    if (contactInfoCardRef.current) {
      contactInfoCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!sellerData) {
    return <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
      <p style={{ fontSize: '1.2rem', color: 'var(--grey-2)' }}>Laddar...</p>
    </div>;
  }

  // Broker or Seller address/phone/email logic
  let displayedAddress = sellerData.address;
  let displayedPhone = sellerInfo ? sellerInfo.phone : '';
  let displayedEmail = sellerInfo ? sellerInfo.email : '';
  let displayedName = sellerInfo ? `${sellerInfo.firstname || ''} ${sellerInfo.lastname || ''}`.trim() : '';

  // If user is broker OR simulating broker view, and display_contact_info = false => hide phone/email, use hidden address
  if ((userType === 'broker' || simulateBrokerView) && !sellerData.display_contact_info) {
    displayedAddress = sellerData.hidden_address || t('objectDashboard.missingAddress');
    displayedPhone = t('common.notProvided');
    displayedEmail = t('common.notProvided');
    displayedName = '';
  }

  // If we're simulating broker view, add a visual indicator
  const simulationBanner = simulateBrokerView && actualUserType === 'seller' ? (
    <div style={{
      backgroundColor: 'var(--duck-blue-8)',
      color: 'var(--duck-blue-1)',
      padding: '15px',
      marginBottom: '20px',
      borderRadius: '8px',
      textAlign: 'center',
      border: '1px solid var(--duck-blue-5)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    }}>
      <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
      {t('sellerDataDetail.brokerPreview')}
      <button
        onClick={() => window.close()}
        style={{
          marginLeft: '15px',
          backgroundColor: 'var(--blue-4)',
          color: 'white',
          border: 'none',
          padding: '8px 15px',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: 'bold',
          transition: 'background-color 0.2s'
        }}
      >
        {t('sellerDataDetail.closePreview')}
      </button>
    </div>
  ) : null;

  return (
    <div className="seller-data-detail" style={{ 
      maxWidth: '1000px', 
      margin: '0 auto', 
      padding: '2rem',
      backgroundColor: 'var(--duck-blue-10)'
    }}>
      {loadingAd && <SpinnerOverlay />}

      {/* Display simulation banner if applicable */}
      {simulationBanner}

      {/* Top contact action is now passed into Ad header as a compact button */}

      {/* Property Information Card */}
      <div className="property-info-card" style={{
        background: 'var(--white)',
        borderRadius: '10px',
        padding: '1.5rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        border: '1px solid var(--duck-blue-7)',
        marginBottom: '2rem'
      }}>
        <div className="property-info-header" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          borderBottom: '1px solid var(--duck-blue-7)',
          paddingBottom: '0.5rem'
        }}>
          <h3 style={{ 
            margin: 0, 
            color: 'var(--blue-1)',
            display: 'flex',
            alignItems: 'center'
          }}>
            <FontAwesomeIcon icon={faHome} style={{ marginRight: '10px', color: 'var(--blue-3)' }} />
            {t('sellerDataDetail.propertyInfo')}
          </h3>
          {userType === 'broker' && !simulateBrokerView && (
            <button
              onClick={handleTopContactClick}
              style={{
                display: 'none',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                backgroundColor: 'var(--blue-3)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.9rem',
              }}
              aria-label={t('sellerDataDetail.contact')}
            >
              <FontAwesomeIcon icon={faComments} />
              <span>{t('sellerDataDetail.contact')}</span>
            </button>
          )}
        </div>
        
        <div className="property-details-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          <div className="property-detail-item">
            <label style={{ 
              display: 'block', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)', 
              marginBottom: '0.25rem',
              fontSize: '0.9rem'
            }}>{t('common.address')}:</label>
            <p style={{ 
              margin: 0, 
              color: 'var(--grey-2)',
              fontSize: '1rem'
            }}>{sellerData.address || t('common.notProvided')}</p>
          </div>
          
          <div className="property-detail-item">
            <label style={{ 
              display: 'block', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)', 
              marginBottom: '0.25rem',
              fontSize: '0.9rem'
            }}>{t('objectForm.propertyType')}:</label>
            <p style={{ 
              margin: 0, 
              color: 'var(--grey-2)',
              fontSize: '1rem'
            }}>{sellerData.property_type ? translatePropertyType(sellerData.property_type, language) : t('common.notProvided')}</p>
          </div>
          
          <div className="property-detail-item">
            <label style={{ 
              display: 'block', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)', 
              marginBottom: '0.25rem',
              fontSize: '0.9rem'
            }}>{t('objectForm.area')}:</label>
            <p style={{ 
              margin: 0, 
              color: 'var(--grey-2)',
              fontSize: '1rem'
            }}>{sellerData.area ? `${sellerData.area} m²` : t('common.notProvided')}</p>
          </div>
          
          <div className="property-detail-item">
            <label style={{ 
              display: 'block', 
              fontWeight: 'bold', 
              color: 'var(--grey-1)', 
              marginBottom: '0.25rem',
              fontSize: '0.9rem'
            }}>{t('objectForm.rooms')}:</label>
            <p style={{ 
              margin: 0, 
              color: 'var(--grey-2)',
              fontSize: '1rem'
            }}>{sellerData.number_of_rooms || t('common.notProvided')}</p>
          </div>
          
          {sellerData.lan && (
            <div className="property-detail-item">
              <label style={{ 
                display: 'block', 
                fontWeight: 'bold', 
                color: 'var(--grey-1)', 
                marginBottom: '0.25rem',
                fontSize: '0.9rem'
              }}>{t('sellerDataDetail.county')}:</label>
              <p style={{ 
                margin: 0, 
                color: 'var(--grey-2)',
                fontSize: '1rem'
              }}>{sellerData.lan}</p>
            </div>
          )}
          
          {sellerData.floor && (
            <div className="property-detail-item">
              <label style={{ 
                display: 'block', 
                fontWeight: 'bold', 
                color: 'var(--grey-1)', 
                marginBottom: '0.25rem',
                fontSize: '0.9rem'
              }}>{t('objectForm.floor')}:</label>
              <p style={{ 
                margin: 0, 
                color: 'var(--grey-2)',
                fontSize: '1rem'
              }}>{sellerData.floor}</p>
            </div>
          )}
          
          {sellerData.monthly_fee && (
            <div className="property-detail-item">
              <label style={{ 
                display: 'block', 
                fontWeight: 'bold', 
                color: 'var(--grey-1)', 
                marginBottom: '0.25rem',
                fontSize: '0.9rem'
              }}>{t('objectForm.monthlyFee')}:</label>
              <p style={{ 
                margin: 0, 
                color: 'var(--grey-2)',
                fontSize: '1rem'
              }}>{sellerData.monthly_fee} kr</p>
            </div>
          )}
          
          {sellerData.plot_size && (
            <div className="property-detail-item">
              <label style={{ 
                display: 'block', 
                fontWeight: 'bold', 
                color: 'var(--grey-1)', 
                marginBottom: '0.25rem',
                fontSize: '0.9rem'
              }}>{t('objectForm.plotSize')}:</label>
              <p style={{ 
                margin: 0, 
                color: 'var(--grey-2)',
                fontSize: '1rem'
              }}>{sellerData.plot_size} m²</p>
            </div>
          )}
          
          {sellerData.construction_year && (
            <div className="property-detail-item">
              <label style={{ 
                display: 'block', 
                fontWeight: 'bold', 
                color: 'var(--grey-1)', 
                marginBottom: '0.25rem',
                fontSize: '0.9rem'
              }}>{t('objectForm.constructionYear')}:</label>
              <p style={{ 
                margin: 0, 
                color: 'var(--grey-2)',
                fontSize: '1rem'
              }}>{sellerData.construction_year}</p>
            </div>
          )}
          
          {sellerData.number_of_floors && (
            <div className="property-detail-item">
              <label style={{ 
                display: 'block', 
                fontWeight: 'bold', 
                color: 'var(--grey-1)', 
                marginBottom: '0.25rem',
                fontSize: '0.9rem'
              }}>{t('objectForm.floors')}:</label>
              <p style={{ 
                margin: 0, 
                color: 'var(--grey-2)',
                fontSize: '1rem'
              }}>{sellerData.number_of_floors}</p>
            </div>
          )}
        </div>
        
        {/* Additional Information Section - only show if renovations or ad content exists */}
        {(sellerData.renovations || sellerData.ad) && (
          <div className="additional-info-section" style={{
          }}>
            {sellerData.renovations && (
              <div className="property-detail-item" style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontWeight: 'bold', 
                  color: 'var(--grey-1)', 
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>{t('sellerDataDetail.condition')}:</label>
                <p style={{ 
                  margin: 0, 
                  color: 'var(--grey-2)',
                  fontSize: '1rem',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}>{sellerData.renovations?.substring(7)}</p>
              </div>
            )}
            
{/*             {sellerData.ad && (
              <div className="property-detail-item">
                <label style={{ 
                  display: 'block', 
                  fontWeight: 'bold', 
                  color: 'var(--grey-1)', 
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem'
                }}>Ytterligare information:</label>
                <p style={{ 
                  margin: 0, 
                  color: 'var(--grey-2)',
                  fontSize: '1rem',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}>{sellerData.ad}</p>
              </div>
            )} */}
          </div>
        )}
      </div>

      {/* Valuation Information */}
      {(sellerData.results || sellerData.object) && (
        <div className="valuation-card" style={{
          background: 'var(--white)',
          borderRadius: '10px',
          padding: '1.5rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--duck-blue-7)',
          marginBottom: '2rem'
        }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '1.5rem', 
            color: '#28a745',
            borderBottom: '1px solid var(--duck-blue-7)',
            paddingBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            fontSize: '1.4rem',
            fontWeight: '600'
          }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '10px', color: '#28a745' }} />
            {t('valuationResult.valuationTitle')}
          </h3>
          <div className="valuation-explanation" style={{
            marginBottom: '1.5rem'
          }}>
            <div style={{ 
              color: '#495057',
              lineHeight: '1.6',
              fontSize: '1.05rem',
              margin: '0',
              whiteSpace: 'pre-wrap', 
              fontFamily: 'inherit'
            }}>{(sellerData.results || sellerData.object || '').replace(/\\n/g, '\n')}</div>
          </div>
          <div className="valuation-disclaimer" style={{
            padding: '15px',
            backgroundColor: 'var(--duck-blue-10)',
            borderRadius: '6px',
            border: '1px solid var(--duck-blue-7)',
            borderLeft: '4px solid #28a745'
          }}>
            <p style={{
              margin: '0',
              color: 'var(--grey-2)'
            }}><small>
              {t('valuationResult.disclaimer')}
            </small></p>
          </div>
        </div>
      )}

      {/* Contact Info Card - Moved between Ad and Image section */}
      {userType !== 'broker' && !simulateBrokerView && (sellerData.results || sellerData.object) && (
        <BodexPlusOffer />
      )}
      {(userType === 'broker' || simulateBrokerView) && (
        <div className="contact-info-card" style={{
          
          background: 'var(--white)',
          borderRadius: '10px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          border: '1px solid var(--duck-blue-7)',
          marginTop: '2rem',
          marginBottom: '2rem'
        }} ref={contactInfoCardRef}>
          {!showConversationForm ? (
            <>
              <h3 style={{ 
                marginTop: '0', 
                marginBottom: '1.5rem', 
                color: 'var(--blue-1)',
                borderBottom: '1px solid var(--duck-blue-7)',
                paddingBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                <FontAwesomeIcon icon={faHome} style={{ marginRight: '10px', color: 'var(--blue-3)' }} />
                {t('sellerLanding.contactInfo')}
              </h3>
              
              <div style={{ marginBottom: '1rem' }}>
                {sellerData.display_contact_info && displayedName && (
                  <p style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    margin: '0.7rem 0',
                    color: 'var(--grey-2)'
                  }}>
                    <FontAwesomeIcon icon={faUser} style={{ 
                      marginRight: '10px', 
                      color: 'var(--blue-3)', 
                      width: '20px' 
                    }} />
                    <span style={{ fontWeight: 'bold', color: 'var(--grey-1)', marginRight: '8px' }}>{t('sellerDataDetail.name')}:</span> 
                    {displayedName}
                  </p>
                )}
                
                <p style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0.7rem 0',
                  color: 'var(--grey-2)'
                }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ 
                    marginRight: '10px', 
                    color: 'var(--blue-3)', 
                    width: '20px' 
                  }} />
                  <span style={{ fontWeight: 'bold', color: 'var(--grey-1)', marginRight: '8px' }}>{t('common.address')}:</span> 
                  {sellerData.display_contact_info ? displayedAddress : displayedAddress}
                </p>
                
                <p style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0.7rem 0',
                  color: 'var(--grey-2)'
                }}>
                  <FontAwesomeIcon icon={faEnvelope} style={{ 
                    marginRight: '10px', 
                    color: 'var(--blue-3)', 
                    width: '20px' 
                  }} />
                  <span style={{ fontWeight: 'bold', color: 'var(--grey-1)', marginRight: '8px' }}>{t('common.email')}:</span> 
                  {displayedEmail}
                </p>
                
                <p style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  margin: '0.7rem 0',
                  color: 'var(--grey-2)'
                }}>
                  <FontAwesomeIcon icon={faPhone} style={{ 
                    marginRight: '10px', 
                    color: 'var(--blue-3)', 
                    width: '20px' 
                  }} />
                  <span style={{ fontWeight: 'bold', color: 'var(--grey-1)', marginRight: '8px' }}>{t('brokerDashboard.phone')}:</span> 
                  {displayedPhone}
                </p>
              </div>
              
              {!simulateBrokerView && (
                <div style={{ marginTop: '1.5rem' }}>
                  <button
                    onClick={() => setShowConversationForm(true)}
                    style={{ 
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      padding: '10px',
                      backgroundColor: 'var(--blue-3)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <FontAwesomeIcon icon={faComments} style={{ marginRight: '8px' }} />
                    {t('sellerDataDetail.contactSeller')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <h3 style={{ 
                marginTop: '0', 
                marginBottom: '1rem', 
                color: 'var(--blue-1)',
                borderBottom: '1px solid var(--duck-blue-7)',
                paddingBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                <FontAwesomeIcon icon={faComments} style={{ marginRight: '10px', color: 'var(--blue-3)' }} />
                {t('brokerProfile.newConversation')}
              </h3>
              <NewConversation
                preselectedSellerId={sellerData.seller_id}
                preselectedObjectLink={`${window.location.origin}/seller-data/${sellerData.id}`}
                streetName={sellerData.address ? sellerData.address.replace(/\s+\d+.*$/, '') : ''}
                onConversationCreated={() => setShowConversationForm(false)}
                onCancel={() => setShowConversationForm(false)}
              />
            </>
          )}
        </div>
      )}

      {/* Image Gallery Section */}
      <div className="image-section" style={{ marginTop: '2rem' }}>
        {sellerImages && sellerImages.length > 0 ? (
          <div className="image-gallery-container" style={{ 
            background: 'var(--duck-blue-10)', 
            borderRadius: '10px',
            padding: '1rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ 
              marginTop: '0', 
              marginBottom: '1rem', 
              color: 'var(--blue-1)',
              borderBottom: '1px solid var(--duck-blue-7)',
              paddingBottom: '0.5rem'
            }}>Bilder</h3>
            
            {userType === 'seller' && !simulateBrokerView ? (
              // If seller => show multiple image upload
              <UnifiedFileUpload
                data_id={sellerData.id}
                existingImages={sellerImages}
                onImagesUpdate={handleImagesUpdate}
                allowMultiple={true}
                delayUpload={false}
                onImageClick={handleImageClick}
              />
            ) : (
              // If broker or simulating broker => just show existing images
              <div className="image-gallery" style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '12px', 
                justifyContent: 'flex-start'
              }}>
                {sellerImages.map((img, index) => (
                  <div 
                    key={index} 
                    style={{ 
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s',
                      cursor: 'pointer',
                      width: '150px',
                      height: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5'
                    }}
                    onClick={() => handleImageClick(img)}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <img
                      src={img}
                      alt={`Property ${index + 1}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : userType === 'seller' && !simulateBrokerView ? (
          <div className="image-upload-container" style={{ 
            background: 'var(--duck-blue-10)', 
            borderRadius: '10px',
            padding: '1rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ 
              marginTop: '0', 
              marginBottom: '1rem', 
              color: 'var(--blue-1)',
              borderBottom: '1px solid var(--duck-blue-7)',
              paddingBottom: '0.5rem'
            }}>Bilder</h3>
            <UnifiedFileUpload
              data_id={sellerData.id}
              existingImages={[]}
              onImagesUpdate={handleImagesUpdate}
              allowMultiple={true}
              delayUpload={false}
              onImageClick={handleImageClick}
            />
          </div>
        ) : null}
      </div>

      {/* Full-size Image Modal */}
      {selectedImage && (
        <div
          className="image-modal-overlay"
          onClick={() => {
            setSelectedImage(null);
            setSelectedImageIndex(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="image-modal-content"
            style={{ 
              position: 'relative', 
              maxWidth: '90%', 
              maxHeight: '90%',
              background: 'transparent',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Full size"
              style={{ 
                maxWidth: '90vw', 
                maxHeight: '90vh', 
                width: 'auto', 
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '8px' 
              }}
            />
            <button
              onClick={() => {
                setSelectedImage(null);
                setSelectedImageIndex(null);
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(255,255,255,0.8)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#333',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            {/* Left arrow button - always visible but conditionally enabled */}
            <button
              ref={leftArrowRef}
              onClick={handlePrevImage}
              disabled={selectedImageIndex <= 0}
              style={{
                position: 'absolute',
                top: '50%',
                left: '15px',
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.8)',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: selectedImageIndex > 0 ? 'pointer' : 'not-allowed',
                fontSize: '24px',
                color: selectedImageIndex > 0 ? '#333' : '#999',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                opacity: selectedImageIndex > 0 ? 1 : 0.5
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            
            {/* Right arrow button - always visible but conditionally enabled */}
            <button
              ref={rightArrowRef}
              onClick={handleNextImage}
              disabled={selectedImageIndex >= sellerImages.length - 1}
              style={{
                position: 'absolute',
                top: '50%',
                right: '15px',
                transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.8)',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: selectedImageIndex < sellerImages.length - 1 ? 'pointer' : 'not-allowed',
                fontSize: '24px',
                color: selectedImageIndex < sellerImages.length - 1 ? '#333' : '#999',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                opacity: selectedImageIndex < sellerImages.length - 1 ? 1 : 0.5
              }}
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            
            {/* Image counter */}
            <div
              style={{
                position: 'absolute',
                bottom: '15px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.6)',
                padding: '8px 15px',
                borderRadius: '20px',
                color: 'white',
                fontSize: '14px'
              }}
            >
              {selectedImageIndex + 1} / {sellerImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Broker-Only View */}
      {(userType === 'broker' || simulateBrokerView) && (
        <div className="broker-view" style={{ marginTop: '2rem' }}>
          {/* Contact and Map Sections Side by Side for Larger Screens */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '1.5rem',
            marginBottom: '1.5rem' 
          }}>
            {/* Map Card - Moved above contact info */}
            <div className="map-card" style={{
              background: 'var(--white)',
              borderRadius: '10px',
              padding: '1.5rem',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              border: '1px solid var(--duck-blue-7)'
            }}>
              <h3 style={{ 
                marginTop: '0', 
                marginBottom: '1rem', 
                color: 'var(--blue-1)',
                borderBottom: '1px solid var(--duck-blue-7)',
                paddingBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '10px', color: 'var(--blue-3)' }} />
                {t('sellerDataDetail.map')}
              </h3>
              
              {sellerData.display_contact_info ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: 'var(--success-2)',
                  marginBottom: '1rem',
                  padding: '8px 12px',
                  backgroundColor: 'var(--success-3)',
                  borderRadius: '6px'
                }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '8px' }} />
                  {t('sellerDataDetail.exactMap')}
                </div>
              ) : (
                <div style={{ 
                  backgroundColor: 'var(--warning-3)',
                  color: 'var(--warning-2)',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #FFEEBA',
                  marginBottom: '1rem',
                  fontSize: '0.95rem'
                }}>
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '8px' }} />
                  {t('sellerDataDetail.approximateMap')}
                </div>
              )}
              
              <SellerDetailsCard 
                data={sellerData} 
                onUpdate={() => {}} 
                readOnly={true} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Seller-Only View - hide in simulation mode */}
      {userType === 'seller' && !simulateBrokerView && (
        <div className="seller-view" style={{ marginTop: '2rem' }}>
          <SellerDetailsCard 
            data={sellerData} 
            onUpdate={handleSellerDataUpdate}
          />

          <div style={{ marginTop: '2rem' }}>
            <SellerButtons
              onTogglePrompt={toggleAdditionalInputForm}
              showPrompt={showAdditionalInputForm}
              onDelete={() => setShowDeleteConfirmation(true)}
              dataId={sellerData.id}
              hidePublishButton={true}
            />
          </div>

          {/* Confirm Delete Modal */}
          {showDeleteConfirmation && (
            <DeleteConfirmationModal
              onDelete={handleDelete}
              onCancel={() => setShowDeleteConfirmation(false)}
            />
          )}
        </div>
      )}

      {error && (
        <div style={{ 
          marginTop: '1.5rem', 
          padding: '1rem', 
          backgroundColor: 'var(--error-3)', 
          color: 'var(--error-2)',
          borderRadius: '8px',
          border: '1px solid var(--error-1)',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default SellerDataDetail;
